/**
 * Editor visual de uma automação (issue #137) — canvas @xyflow/react.
 *
 * Layout: paleta de passos à esquerda (clique ou arraste para o canvas),
 * canvas no centro e painel de configuração do passo selecionado à direita.
 * A aba "Execuções" troca o canvas pelo histórico com log por passo.
 *
 * O fluxo é salvo como o próprio grafo do xyflow ({ nodes, edges }) — o
 * backend valida e executa esse mesmo formato. Rascunho com erro pode ser
 * salvo; ativar exige o fluxo válido (o backend recusa com a lista de erros).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Background, ControlButton, Controls, MarkerType, MiniMap, ReactFlow, ReactFlowProvider,
  addEdge, useEdgesState, useNodesState, useReactFlow,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import Layout from '../../components/Layout.jsx'
import Icon from '../../components/icons.jsx'
import { useApp } from '../../context/AppContext.jsx'
import {
  autCatalog, autGet, autLeadForms, autUpdate, autWebhookUrl, getCrmMembers, getPipelines, getSaldos, getStages,
  listWhatsappInstances,
} from '../../db/api.js'
import AutomacaoNode, { EditorContext } from './AutomacaoNode.jsx'
import AutomacaoTeste from './AutomacaoTeste.jsx'
import AutomacaoConfigPanel from './AutomacaoConfigPanel.jsx'
import AutomacaoLeadsPendentes from './AutomacaoLeadsPendentes.jsx'
import AutomacaoExecucoes from './AutomacaoExecucoes.jsx'
import { PALETTE_GROUPS, hasConfigProblem, metaFor, nodeHandles, paletteSections } from './nodeMeta.js'
import { STEP_Y, firstFreeOutput, layoutLayers } from './flowLayout.js'
import './automacoes.css'

/** Caminhos folha do JSON recebido: {a:{b:1}, itens:[{n:2}]} → ["a.b", "itens.0.n"]. */
function flattenSample(value, prefix = '', out = []) {
  if (out.length >= 80) return out
  if (value && typeof value === 'object') {
    Object.entries(value).forEach(([k, v]) => flattenSample(v, prefix ? `${prefix}.${k}` : k, out))
  } else if (prefix) {
    out.push(prefix)
  }
  return out
}

const NODE_TYPES = { automation: AutomacaoNode }
const DRAG_MIME = 'application/x-titam-automation-node'

const DEFAULT_EDGE = {
  type: 'smoothstep',
  markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
}

let seq = 0
const newId = () => `n${Date.now().toString(36)}${(seq++).toString(36)}`

// Só o que o backend precisa: sem estado de UI do xyflow (selected, measured…).
function toDefinition(nodes, edges) {
  return {
    nodes: nodes.map((n) => ({
      id: n.id, type: 'automation',
      position: { x: Math.round(n.position.x), y: Math.round(n.position.y) },
      data: { nodeType: n.data.nodeType, config: n.data.config || {} },
    })),
    edges: edges.map((e) => ({
      id: e.id, source: e.source, target: e.target,
      ...(e.sourceHandle ? { sourceHandle: e.sourceHandle } : {}),
    })),
  }
}

// Rótulo das arestas vem das saídas do nó de origem (Sim/Não, Respondeu,
// valor do switch) — calculado na renderização, não salvo no definition.
function edgeView(e) {
  return {
    ...DEFAULT_EDGE, ...e,
    className: e.sourceHandle ? `aut-edge-${e.sourceHandle}` : undefined,
  }
}

function Editor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { selectedClient, toast } = useApp()
  const clientId = selectedClient?.id || null
  const { screenToFlowPosition, fitView } = useReactFlow()

  const [automation, setAutomation] = useState(null)
  const [catalog, setCatalog] = useState({})
  const [busca, setBusca] = useState('')
  const [abertos, setAbertos] = useState(() => new Set()) // grupos da paleta expandidos (kind:módulo)
  const [loadError, setLoadError] = useState(null)
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])
  const [selectedId, setSelectedId] = useState(null)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [tab, setTab] = useState('editor')
  const [nome, setNome] = useState('')
  const [serverErrors, setServerErrors] = useState([])
  const [testOpen, setTestOpen] = useState(false)
  const [testRun, setTestRun] = useState(null)   // último resultado do modo de teste

  // Listas dos selects dinâmicos do painel.
  const [pipelines, setPipelines] = useState([])
  const [stagesByPipeline, setStagesByPipeline] = useState({})
  const [members, setMembers] = useState([])
  const [instances, setInstances] = useState([])
  const [contasFinanceiras, setContasFinanceiras] = useState([])   // #169
  const [leadForms, setLeadForms] = useState([])   // #301
  const stagesLoading = useRef(new Set())

  const loadStages = useCallback((pipelineId) => {
    if (!pipelineId || stagesLoading.current.has(pipelineId)) return
    stagesLoading.current.add(pipelineId)
    getStages(pipelineId)
      .then((rows) => setStagesByPipeline((cur) => ({ ...cur, [pipelineId]: rows })))
      .catch(() => stagesLoading.current.delete(pipelineId))
  }, [])

  // ---------------------------------------------------------------- carga
  useEffect(() => {
    if (!clientId) return undefined
    let vivo = true
    Promise.all([autGet(id), autCatalog()])
      .then(([row, cat]) => {
        if (!vivo) return
        setAutomation(row)
        setNome(row.nome)
        setServerErrors(row.errors || [])
        setCatalog(Object.fromEntries(cat.nodes.map((n) => [n.key, n])))
        const def = row.definition || { nodes: [], edges: [] }
        setNodes(def.nodes.map((n) => ({ ...n, type: 'automation' })))
        setEdges(def.edges.map(edgeView))
        def.nodes.forEach((n) => loadStages(n.data?.config?.pipeline_id))
        setTimeout(() => fitView({ padding: 0.3, maxZoom: 1 }), 50)
      })
      .catch((e) => vivo && setLoadError(e.message))

    getPipelines('comercial').then((r) => vivo && setPipelines(r)).catch(() => {})
    getCrmMembers().then((r) => vivo && setMembers(r)).catch(() => {})
    getSaldos().then((r) => vivo && setContasFinanceiras(r || [])).catch(() => {})   // #169
    autLeadForms(clientId).then((r) => vivo && setLeadForms(r || [])).catch(() => {})   // #301
    listWhatsappInstances()
      .then((r) => vivo && setInstances((r || []).filter((i) => i.client_id === clientId)))
      .catch(() => {})
    return () => { vivo = false }
  }, [id, clientId, setNodes, setEdges, loadStages, fitView])

  // Aviso ao fechar a aba com alteração não salva.
  useEffect(() => {
    if (!dirty) return undefined
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  // ------------------------------------------------------------ derivados
  const names = useMemo(() => {
    const map = {}
    pipelines.forEach((p) => { map[p.id] = p.nome })
    members.forEach((m) => { map[m.id] = m.nome })
    Object.values(stagesByPipeline).flat().forEach((s) => { map[s.id] = s.nome })
    return map
  }, [pipelines, stagesByPipeline, members])

  const invalid = useMemo(
    () => new Set(nodes.filter((n) => hasConfigProblem(catalog[n.data.nodeType], n.data.config)).map((n) => n.id)),
    [nodes, catalog],
  )

  // Status de cada passo no último teste (nós que não rodaram ficam sem marca).
  const testSteps = useMemo(
    () => Object.fromEntries((testRun?.steps || []).map((s) => [s.node_id, s])),
    [testRun],
  )
  const editorCtx = useMemo(() => ({ catalog, names, invalid, testSteps }), [catalog, names, invalid, testSteps])

  const displayEdges = useMemo(() => {
    const byId = new Map(nodes.map((n) => [n.id, n]))
    return edges.map((e) => {
      if (!e.sourceHandle) return e
      const src = byId.get(e.source)
      const h = nodeHandles(catalog[src?.data.nodeType], src?.data.config)?.find((x) => x.id === e.sourceHandle)
      return h ? { ...e, label: h.label } : e
    })
  }, [edges, nodes, catalog])
  const hasTrigger = nodes.some((n) => catalog[n.data.nodeType]?.kind === 'trigger')
  const selectedNode = nodes.find((n) => n.id === selectedId) || null

  const sourceOptions = useCallback((source, parent) => {
    switch (source) {
      case 'pipelines': return pipelines.map((p) => ({ value: p.id, label: p.nome }))
      case 'stages':
        loadStages(parent)
        return (stagesByPipeline[parent] || []).map((s) => ({ value: s.id, label: s.nome }))
      case 'members': return members.map((m) => ({ value: m.id, label: m.nome || 'Sem nome' }))
      case 'contas_financeiras':   // #169
        return contasFinanceiras.map((c) => ({ value: c.id, label: c.nome }))
      case 'meta_lead_forms':   // #301
        return leadForms
      case 'whatsapp_instances':
        return instances.map((i) => ({
          value: i.instance_name,
          label: `${i.phone || i.instance_name}${i.status === 'open' ? '' : ' (desconectado)'}`,
        }))
      default: return []
    }
  }, [pipelines, stagesByPipeline, members, instances, contasFinanceiras, leadForms, loadStages])

  // Variáveis que o seletor oferece: campos fixos (os mesmos da condição) e os
  // campos do último corpo recebido pelo webhook de entrada.
  const variables = useMemo(() => {
    const fixas = (catalog['flow.condition']?.fields || [])
      .find((f) => f.type === 'rules')?.fields?.filter((f) => f.value && !f.value.endsWith('.')) || []
    return [
      ...fixas,
      ...flattenSample(automation?.webhook_sample).map((p) => ({ value: `webhook.${p}`, label: `Webhook — ${p}` })),
    ]
  }, [catalog, automation?.webhook_sample])

  const refreshSample = useCallback(async () => {
    try {
      const row = await autGet(id)
      setAutomation((cur) => ({ ...cur, webhook_sample: row.webhook_sample, webhook_sample_at: row.webhook_sample_at }))
    } catch (e) { toast?.(e.message, 'error') }
  }, [id, toast])

  // ------------------------------------------------------------- edição
  const markDirty = () => setDirty(true)

  const handleNodesChange = useCallback((changes) => {
    onNodesChange(changes)
    if (changes.some((c) => c.type === 'position' || c.type === 'remove')) setDirty(true)
    const removed = changes.find((c) => c.type === 'remove' && c.id === selectedId)
    if (removed) setSelectedId(null)
  }, [onNodesChange, selectedId])

  const handleEdgesChange = useCallback((changes) => {
    onEdgesChange(changes)
    if (changes.some((c) => c.type === 'remove')) setDirty(true)
  }, [onEdgesChange])

  const onConnect = useCallback((conn) => {
    if (conn.source === conn.target) return
    setEdges((eds) => {
      // Um handle de saída ligado duas vezes ao mesmo destino não faz sentido.
      const dup = eds.some((e) => e.source === conn.source && e.target === conn.target
        && (e.sourceHandle || null) === (conn.sourceHandle || null))
      if (dup) return eds
      const idEdge = `e-${conn.source}-${conn.sourceHandle || 'o'}-${conn.target}`
      return addEdge(edgeView({ ...conn, id: idEdge }), eds)
    })
    setDirty(true)
  }, [setEdges])

  const addNode = useCallback((nodeType, position = null) => {
    const type = catalog[nodeType]
    if (!type) return
    if (type.kind === 'trigger' && hasTrigger) {
      toast?.('O fluxo já tem um gatilho. Remova o atual para trocar.', 'error')
      return
    }
    const anchor = nodes.find((n) => n.id === selectedId) || nodes[nodes.length - 1]
    const pos = position || (anchor
      ? { x: anchor.position.x, y: anchor.position.y + STEP_Y }
      : { x: 0, y: 0 })
    const novo = { id: newId(), type: 'automation', position: pos, data: { nodeType, config: {} } }
    setNodes((cur) => [...cur.map((n) => ({ ...n, selected: false })), { ...novo, selected: true }])
    // Clique ou arrasto da paleta já conecta ao anterior (fluxo linear rápido), na
    // primeira saída ainda livre: nós com várias saídas (Condição, Switch…) não
    // repetem a mesma. Sem saída livre, o nó entra solto. Gatilho não tem entrada.
    if (anchor && type.kind !== 'trigger') {
      const livre = firstFreeOutput(anchor.id, nodeHandles(catalog[anchor.data.nodeType], anchor.data.config), edges)
      if (livre) {
        const handle = livre.handle
        setEdges((eds) => addEdge(edgeView({
          id: `e-${anchor.id}-${handle || 'o'}-${novo.id}`, source: anchor.id, target: novo.id,
          ...(handle ? { sourceHandle: handle } : {}),
        }), eds))
      }
    }
    setSelectedId(novo.id)
    setDirty(true)
  }, [catalog, hasTrigger, nodes, edges, selectedId, setNodes, setEdges, toast])

  const onDrop = useCallback((e) => {
    e.preventDefault()
    const nodeType = e.dataTransfer.getData(DRAG_MIME)
    if (!nodeType) return
    addNode(nodeType, screenToFlowPosition({ x: e.clientX, y: e.clientY }))
  }, [addNode, screenToFlowPosition])

  // Reorganiza em camadas seguindo as conexões; só muda posição, nunca as arestas.
  const alignNodes = useCallback(() => {
    if (!nodes.length) return
    const pos = layoutLayers(nodes, edges)
    setNodes((cur) => cur.map((n) => (pos[n.id] ? { ...n, position: pos[n.id] } : n)))
    setDirty(true)
    setTimeout(() => fitView({ padding: 0.3, maxZoom: 1, duration: 300 }), 50)
  }, [nodes, edges, setNodes, fitView])

  const updateConfig = (config) => {
    setNodes((cur) => cur.map((n) => (n.id === selectedId ? { ...n, data: { ...n.data, config } } : n)))
    // Switch: caminho removido leva junto as conexões que saíam dele.
    const handles = nodeHandles(catalog[selectedNode?.data.nodeType], config)
    if (handles) {
      const validos = new Set(handles.map((h) => h.id))
      setEdges((cur) => cur.filter((e) => e.source !== selectedId || validos.has(e.sourceHandle)))
    }
    if (config.pipeline_id) loadStages(config.pipeline_id)
    markDirty()
  }

  const removeSelected = () => {
    setNodes((cur) => cur.filter((n) => n.id !== selectedId))
    setEdges((cur) => cur.filter((e) => e.source !== selectedId && e.target !== selectedId))
    setSelectedId(null)
    markDirty()
  }

  // ------------------------------------------------------------- salvar
  const save = async (extra = {}) => {
    setSaving(true)
    try {
      const row = await autUpdate(id, {
        nome: nome.trim() || 'Sem nome',
        definition: toDefinition(nodes, edges),
        ...extra,
      })
      setAutomation(row)
      setServerErrors(row.errors || [])
      setDirty(false)
      return row
    } catch (e) {
      if (e.detail?.errors) setServerErrors(e.detail.errors)
      toast?.(e.message, 'error')
      return null
    } finally {
      setSaving(false)
    }
  }

  const onSave = async () => {
    const row = await save()
    if (row) toast?.('Automação salva', 'success')
  }

  const toggleAtivo = async () => {
    const row = await save({ ativo: !automation.ativo })
    if (row) toast?.(row.ativo ? 'Automação ativada' : 'Automação desativada', 'success')
  }

  // Atalho Ctrl/Cmd+S.
  const saveRef = useRef(onSave)
  saveRef.current = onSave
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault()
        saveRef.current()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // ---------------------------------------------------------------- tela
  const breadcrumb = [{ label: 'Automações', to: '/automacoes' }, { label: automation?.nome || '…' }]

  if (!clientId) {
    return <Layout title="Automações" breadcrumb={breadcrumb}><div className="aut-empty">Selecione um cliente.</div></Layout>
  }
  if (loadError) {
    return (
      <Layout title="Automações" breadcrumb={breadcrumb}>
        <div className="aut-empty">
          <span className="aut-error-text">{loadError}</span>
          <button type="button" className="btn btn-outline btn-sm" onClick={() => navigate('/automacoes')}>Voltar</button>
        </div>
      </Layout>
    )
  }
  if (!automation) {
    return <Layout title="Automações" breadcrumb={breadcrumb}><div className="aut-empty">Carregando…</div></Layout>
  }

  // Checagem rápida no front; a palavra final é do backend (422 com os erros).
  const podeAtivar = automation.ativo || (hasTrigger && invalid.size === 0)

  return (
    <Layout title="Automações" breadcrumb={breadcrumb}>
      <div className="aut-editor">
        <div className="aut-toolbar">
          <input
            className="aut-name" value={nome} aria-label="Nome da automação"
            onChange={(e) => { setNome(e.target.value); markDirty() }}
          />
          <div className="aut-seg" role="tablist" aria-label="Visão">
            <button type="button" role="tab" aria-selected={tab === 'editor'}
              className={tab === 'editor' ? 'is-active' : ''} onClick={() => setTab('editor')}>
              <Icon n="grid" s={13} /> Editor
            </button>
            <button type="button" role="tab" aria-selected={tab === 'execucoes'}
              className={tab === 'execucoes' ? 'is-active' : ''} onClick={() => setTab('execucoes')}>
              <Icon n="timeline" s={13} /> Execuções
            </button>
          </div>
          <div className="aut-toolbar-right">
            {dirty && <span className="aut-dirty">Alterações não salvas</span>}
            <label className={`aut-switch-label${podeAtivar ? '' : ' is-disabled'}`}
              title={podeAtivar ? undefined : 'Corrija os passos destacados para ativar'}>
              <span className="aut-switch">
                <input type="checkbox" checked={!!automation.ativo} disabled={saving || !podeAtivar}
                  onChange={toggleAtivo} />
                <span />
              </span>
              {automation.ativo ? 'Ativa' : 'Inativa'}
            </label>
            <button type="button" className={`btn btn-sm ${testOpen ? 'btn-black' : 'btn-outline'}`}
              onClick={() => setTestOpen((v) => !v)} disabled={!hasTrigger}
              title={hasTrigger ? 'Executar o fluxo agora para testar' : 'Adicione um gatilho para testar'}>
              <Icon n="zap" s={13} /> Testar
            </button>
            <button type="button" className="btn btn-black btn-sm" onClick={onSave} disabled={saving || !dirty}>
              {saving ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </div>

        {automation.trigger_type === 'lead_ad_form_received' && automation.ativo && (
          <AutomacaoLeadsPendentes automationId={id} disabled={dirty} />
        )}

        {tab === 'execucoes' ? (
          <AutomacaoExecucoes automationId={id} catalog={catalog} />
        ) : (
          <div className={`aut-workspace${selectedNode || testOpen ? ' has-panel' : ''}`}>
            <aside className="aut-palette" aria-label="Passos disponíveis">
              <input
                type="search" className="aut-palette-search" value={busca} placeholder="Buscar passo…"
                aria-label="Buscar gatilho, lógica ou ação" onChange={(e) => setBusca(e.target.value)}
              />
              {PALETTE_GROUPS.map((g) => {
                const secoes = paletteSections(catalog, g.kind, busca)
                if (!secoes.length) return null
                return (
                  <section key={g.kind}>
                    <h4 className="t-label">{g.label}</h4>
                    {secoes.map(({ module, items }) => {
                      const id = `${g.kind}:${module}`
                      return (
                        <details
                          key={id} className="aut-palette-group" open={Boolean(busca.trim()) || abertos.has(id)}
                          onToggle={(e) => {
                            const open = e.currentTarget.open
                            if (busca.trim() || open === abertos.has(id)) return
                            setAbertos((prev) => {
                              const next = new Set(prev)
                              if (open) next.add(id); else next.delete(id)
                              return next
                            })
                          }}
                        >
                          <summary>{module} <span className="aut-palette-count">{items.length}</span></summary>
                          {items.map((t) => {
                            const meta = metaFor(t)
                            const bloqueado = t.kind === 'trigger' && hasTrigger
                            return (
                              <button
                                key={t.key} type="button" draggable={!bloqueado}
                                className={`aut-palette-item tone-${meta.tone}`}
                                disabled={bloqueado}
                                title={bloqueado ? 'O fluxo já tem um gatilho' : t.description}
                                onDragStart={(e) => {
                                  e.dataTransfer.setData(DRAG_MIME, t.key)
                                  e.dataTransfer.effectAllowed = 'move'
                                }}
                                onClick={() => addNode(t.key)}
                              >
                                <span className="aut-node-icon"><Icon n={meta.icon} s={14} /></span>
                                <span>{t.label}</span>
                              </button>
                            )
                          })}
                        </details>
                      )
                    })}
                  </section>
                )
              })}
              {Boolean(busca.trim()) && PALETTE_GROUPS.every((g) => !paletteSections(catalog, g.kind, busca).length) && (
                <p className="aut-palette-hint">Nenhum passo encontrado para “{busca.trim()}”.</p>
              )}
              <p className="aut-palette-hint">Clique para adicionar abaixo do passo selecionado, ou arraste para o canvas.</p>
            </aside>

            <div className="aut-canvas" onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move' }} onDrop={onDrop}>
              {serverErrors.length > 0 && (
                <div className="aut-alert aut-alert-error aut-canvas-alert" role="status">
                  <Icon n="alert" s={15} />
                  <div>
                    <strong>Para ativar, corrija:</strong>
                    <ul>{serverErrors.map((err) => <li key={err}>{err}</li>)}</ul>
                  </div>
                </div>
              )}
              {!nodes.length && (
                <div className="aut-canvas-empty">
                  <Icon n="zap" s={26} c="var(--ter)" />
                  <strong>Comece pelo gatilho</strong>
                  <span>Escolha na lateral o que dispara esta automação.</span>
                </div>
              )}
              <EditorContext.Provider value={editorCtx}>
                <ReactFlow
                  nodes={nodes} edges={displayEdges} nodeTypes={NODE_TYPES}
                  onNodesChange={handleNodesChange} onEdgesChange={handleEdgesChange}
                  onConnect={onConnect}
                  onNodeClick={(_, n) => setSelectedId(n.id)}
                  onPaneClick={() => setSelectedId(null)}
                  defaultEdgeOptions={DEFAULT_EDGE}
                  deleteKeyCode={['Backspace', 'Delete']}
                  minZoom={0.3} maxZoom={1.5}
                  proOptions={{ hideAttribution: false }}
                >
                  <Background gap={20} size={1.2} color="#d2d2d7" />
                  <Controls showInteractive={false}>
                    <ControlButton onClick={alignNodes} title="Alinhar automaticamente" aria-label="Alinhar automaticamente">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="3" width="6" height="5" rx="1" />
                        <rect x="3" y="16" width="6" height="5" rx="1" />
                        <rect x="15" y="16" width="6" height="5" rx="1" />
                        <path d="M12 8v4M6 16v-4h12v4" />
                      </svg>
                    </ControlButton>
                  </Controls>
                  <MiniMap pannable zoomable className="aut-minimap" nodeBorderRadius={10} />
                </ReactFlow>
              </EditorContext.Provider>
            </div>

            {testOpen && (
              <AutomacaoTeste
                automationId={id} catalog={catalog} result={testRun} dirty={dirty}
                triggerKey={nodes.find((n) => catalog[n.data.nodeType]?.kind === 'trigger')?.data.nodeType}
                webhookToken={automation.webhook_token} sample={automation.webhook_sample}
                getDefinition={() => toDefinition(nodes, edges)}
                onResult={setTestRun}
                onSampleChanged={(row) => setAutomation((cur) => ({
                  ...cur, webhook_sample: row.webhook_sample, webhook_sample_at: row.webhook_sample_at }))}
                onClose={() => setTestOpen(false)}
              />
            )}

            {selectedNode && !testOpen && (
              <AutomacaoConfigPanel
                key={selectedNode.id}
                node={selectedNode} type={catalog[selectedNode.data.nodeType]}
                sourceOptions={sourceOptions} onChange={updateConfig}
                onDelete={removeSelected} onClose={() => setSelectedId(null)}
                webhookUrl={autWebhookUrl(automation.webhook_token)}
                variables={variables} webhookSample={automation.webhook_sample}
                onRefreshSample={refreshSample} testStep={testSteps[selectedNode.id]}
              />
            )}
          </div>
        )}
      </div>
    </Layout>
  )
}

export default function AutomacaoEditor() {
  return (
    <ReactFlowProvider>
      <Editor />
    </ReactFlowProvider>
  )
}
